import { WellKnownClaims } from './well-known-claims';

/**
 * Represents a JSON Web Token
 */
export class Jwt {
  /**
   * The raw encoded version of the token
   */
  public encoded: string | null = null;
  /**
   * The parsed header
   */
  public header: any = null;
  /**
   * The parsed body
   */
  public body: any = null;
  /**
   * The signature of the JWT
   */
  public signature: string | null = null;

  constructor (jwt: string) {
    if (!jwt) {
      throw new Error('Jwt string was not defined');
    }

    this.parseJwt(jwt);
  }

  /**
   * Retrieves the value of the specified claim
   * @param claimName The name of the claim to retrieve
   * @returns Returns the string or string array representation of the claim if it exists, otherwise null
   */
  public getClaim(claimName: string): string | string[] | null {
    if (claimName !== null && this.body !== null && this.body[ claimName ] !== undefined)
      return this.body[ claimName ];
    return null;
  }

  /**
   * Retrieves the value of a well-known claim
   * @param claim The well-known claim to retrieve
   * @returns Returns the string or string array representation of the claim if it exists, otherwise null
   */
  public getWellKnownClaim(claim: WellKnownClaims): string | string[] | null {
    return this.getClaim(claim);
  }

  /**
   * Determines if the location specified has security tokens
   * @param location The full location to check for tokens
   */
  public static hasTokens(location: string): boolean {
    return location.indexOf('access_token=') >= 0 || location.indexOf('id_token=') >= 0;
  }

  /**
   * Parses and decodes the JWT
   * @param jwt The raw jwt
   * @see https://tools.ietf.org/html/draft-ietf-oauth-json-web-token-32
   */
  private parseJwt(jwt: string): void {
    const jwtParts = jwt.split('.');

    // An unsigned jwt still has 3 parts (empty string signature)
    if (jwtParts.length !== 3) {
      throw new Error('Token is not formatted correctly for a jwt');
    }

    this.header = this.decodePart(jwtParts[ 0 ]);
    this.body = this.decodePart(jwtParts[ 1 ]);
    this.signature = jwtParts[ 2 ];

    this.encoded = jwt;
  }

  /**
   * Base64 decodes the specified value
   * @param part The individual JWT section
   */
  private decodePart(part: string): any {
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = window.atob(base64);
    return JSON.parse(decoded);
  }

  /**
   * Retrieves the scopes listed within the JWT
   */
  public get scopes(): string[] {
    if (this.body === null) {
      return [];
    }

    let scopes: any = this.body[ 'scp' ];
    let scopeType = typeof (scopes);

    if (scopeType === 'string') {
      scopes = (scopes as string).split(' ');
      scopeType = typeof (scopes);
    }

    if (scopeType === 'object' && scopes.length) {
      for (let i = scopes.length - 1; i >= 0; i--) {
        if (scopes[ i ] === 'openid') {
          scopes.splice(i);
        }
      }
    }

    return scopes;
  }

  /**
   *  Determines if token is expired
   */
  public isExpired(): boolean {
    const currentDate = new Date().valueOf();
    let expirationDate = 0;

    if (this.body && this.body.exp) {
      expirationDate = this.body.exp * 1000; //for milliseconds
    }

    return expirationDate < currentDate;
  }
}
